import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Table, Tag, Typography, Select, Space, Button, Input, DatePicker, Modal, Switch, EmptyState, Row, Col, theme, Tooltip, message,
} from '../../ui';
import { Plus, RefreshCw, MessageSquare, Search } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIst, formatIstDate } from '../../lib/time';

const SENTIMENTS = [
  { value: 'POSITIVE', label: '🙂 Positive', color: 'green' },
  { value: 'NEUTRAL', label: '😐 Neutral', color: 'neutral' },
  { value: 'NEGATIVE', label: '🙁 Negative', color: 'red' },
];
const SENT_MAP = Object.fromEntries(SENTIMENTS.map((s) => [s.value, s]));

export function FeedbackPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const canAdd = usePermission('FEEDBACK.CREATE');

  const [sentiment, setSentiment] = useState<string | undefined>();
  const [customerType, setCustomerType] = useState<string | undefined>();
  const [followUp, setFollowUp] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [addOpen, setAddOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (sentiment) p.sentiment = sentiment;
    if (customerType) p.customerType = customerType;
    if (followUp) p.followUp = 'true';
    return p;
  }, [sentiment, customerType, followUp]);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['feedback', params],
    queryFn: () => request<any[]>({ url: '/feedback', params }),
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = data ?? [];
    if (!q) return all;
    return all.filter((r) =>
      (r.customerName ?? '').toLowerCase().includes(q) ||
      (r.remark ?? '').toLowerCase().includes(q) ||
      (r.byName ?? '').toLowerCase().includes(q));
  }, [data, search]);

  // Reduce over `rows` (the same array the table shows, incl. the client-side search) so the cards
  // always match the visible list instead of counting rows hidden by the search.
  const kpis = useMemo(() => {
    const all = rows;
    return {
      total: all.length,
      positive: all.filter((r) => r.sentiment === 'POSITIVE').length,
      negative: all.filter((r) => r.sentiment === 'NEGATIVE').length,
      followUps: all.filter((r) => r.followUpNeeded).length,
    };
  }, [rows]);

  const columns: any[] = [
    {
      title: 'Customer', key: 'customer', width: 220,
      render: (_: any, r: any) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{r.customerName ?? <span style={{ color: '#bbb' }}>#{r.customerId}</span>}</span>
          <Tag tone={r.customerType === 'RETAILER' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
            {r.customerType === 'RETAILER' ? 'Retailer' : 'Farmer'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Sentiment', dataIndex: 'sentiment', width: 120,
      render: (s: string) => (s ? <Tag tone={SENT_MAP[s]?.color as any}>{SENT_MAP[s]?.label ?? s}</Tag> : <span style={{ color: '#ccc' }}>—</span>),
    },
    { title: 'Remark', dataIndex: 'remark', ellipsis: true, render: (v: string) => v || <span style={{ color: '#ccc' }}>—</span> },
    {
      title: 'Follow-up', key: 'followUp', width: 150,
      render: (_: any, r: any) => (r.followUpNeeded
        ? <Tag tone="gold">{r.followUpDate ? `By ${formatIstDate(r.followUpDate)}` : 'Needed'}</Tag>
        : <span style={{ color: '#ccc' }}>—</span>),
    },
    {
      title: 'Source', dataIndex: 'source', width: 130,
      render: (s: string, r: any) => (s === 'VISIT'
        ? <Tooltip label={r.visitNo ? `Visit ${r.visitNo}` : 'From a visit'}><Tag tone="blue">Visit</Tag></Tooltip>
        : <Tag tone="green">Manual</Tag>),
    },
    { title: 'By', dataIndex: 'byName', width: 150, render: (v: string) => v || <span style={{ color: '#ccc' }}>—</span> },
    { title: 'Date', dataIndex: 'at', width: 170, defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => String(a.at ?? '').localeCompare(String(b.at ?? '')),
      render: (v: string) => (v ? formatIst(v) : '—') },
  ];

  const Kpi = ({ label, value, color, bg }: { label: string; value: number; color: string; bg: string }) => (
    <div className="rounded-card border border-line" style={{ borderLeft: `4px solid ${color}`, background: bg, padding: '10px 14px' }}>
      <div className="text-xs" style={{ color: token.colorTextSecondary }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}><MessageSquare size={16} /> Feedback</Typography.Title>}
        {canAdd && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>Add feedback</Button>}
      </div>
      {!embedded && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Customer feedback captured during visits or logged here directly.
        </Typography.Paragraph>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Kpi label="Total feedback" value={kpis.total} color="#1677FF" bg="#E6F4FF" /></Col>
        <Col xs={12} md={6}><Kpi label="Positive" value={kpis.positive} color="#1F8A4C" bg="#F6FFED" /></Col>
        <Col xs={12} md={6}><Kpi label="Negative" value={kpis.negative} color="#D9363E" bg="#FFF1F0" /></Col>
        <Col xs={12} md={6}><Kpi label="Follow-ups needed" value={kpis.followUps} color="#D48806" bg="#FFFBE6" /></Col>
      </Row>

      <Card density="compact">
        <Space wrap size={8} style={{ marginBottom: 12 }}>
          <Input prefix={<Search size={16} />} placeholder="Search customer, remark, staff" className="w-60"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <Select allowClear placeholder="Sentiment" style={{ width: 150 }} value={sentiment} onChange={setSentiment}
            options={SENTIMENTS.map((s) => ({ value: s.value, label: s.label }))} />
          <Select allowClear placeholder="Customer type" style={{ width: 150 }} value={customerType} onChange={setCustomerType}
            options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
          <Button variant={followUp ? 'primary' : 'default'} onClick={() => setFollowUp((v) => !v)}>Follow-up needed</Button>
          <Tooltip label="Refresh"><Button icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} aria-label="Refresh" /></Tooltip>
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={rows}
          columns={columns}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} feedback` }}
          locale={{ emptyText: <EmptyState title="No feedback yet" /> }}
        />
      </Card>

      {addOpen && <AddFeedbackModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['feedback'] }); message.success('Feedback added'); }} />}
    </div>
  );
}

function AddFeedbackModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [customerType, setCustomerType] = useState<'RETAILER' | 'FARMER'>('RETAILER');
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [sentiment, setSentiment] = useState<string | undefined>();
  const [remark, setRemark] = useState('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(null);

  const { data: retailers } = useQuery({
    queryKey: ['retailers-fb'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: customerType === 'RETAILER',
  });
  const { data: farmers } = useQuery({
    queryKey: ['farmers-fb'],
    queryFn: () => request<any>({ url: '/farmers', params: { pageSize: 200 } }),
    enabled: customerType === 'FARMER',
  });
  const options = useMemo(() => {
    if (customerType === 'RETAILER') {
      return (retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName} (${r.code})` }));
    }
    return (farmers?.data ?? []).map((f: any) => ({ value: String(f.id), label: `${f.fullName}${f.mobile ? ` · ${f.mobile}` : ''}` }));
  }, [customerType, retailers, farmers]);

  const save = useMutation({
    mutationFn: () => request({
      url: '/feedback', method: 'POST',
      data: {
        customerType, customerId,
        sentiment: sentiment || undefined,
        remark: remark.trim() || undefined,
        followUpNeeded,
        followUpDate: followUpNeeded && followUpDate ? followUpDate.format('YYYY-MM-DD') : undefined,
      },
    }),
    onSuccess: onSaved,
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to add feedback'),
  });

  const canSubmit = !!customerId && (!!sentiment || !!remark.trim());

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Add feedback"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!canSubmit} onClick={() => save.mutate()}>Save</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div>
          <div className="text-xs" style={{ marginBottom: 4 }}>Customer type</div>
          <Select style={{ width: '100%' }} value={customerType}
            onChange={(v) => { setCustomerType(v); setCustomerId(undefined); }}
            options={[{ value: 'RETAILER', label: 'Retailer' }, { value: 'FARMER', label: 'Farmer' }]} />
        </div>
        <div>
          <div className="text-xs" style={{ marginBottom: 4 }}>Customer <span style={{ color: '#D9363E' }}>*</span></div>
          <Select showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder={`Select a ${customerType.toLowerCase()}`}
            value={customerId} onChange={setCustomerId} options={options} />
        </div>
        <div>
          <div className="text-xs" style={{ marginBottom: 4 }}>Sentiment</div>
          <Select allowClear style={{ width: '100%' }} placeholder="Select" value={sentiment} onChange={setSentiment}
            options={SENTIMENTS.map((s) => ({ value: s.value, label: s.label }))} />
        </div>
        <div>
          <div className="text-xs" style={{ marginBottom: 4 }}>Remark</div>
          <Input.TextArea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="What did the customer say?" />
        </div>
        <Space>
          <Switch checked={followUpNeeded} onCheckedChange={setFollowUpNeeded} /> Follow-up needed
          {followUpNeeded && <DatePicker value={followUpDate} onChange={setFollowUpDate} placeholder="Follow-up date" />}
        </Space>
        <Typography.Text type="secondary" className="text-xs">Add a sentiment or a remark (at least one).</Typography.Text>
      </Space>
    </Modal>
  );
}
